import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Calendar, X, CreditCard, AlertCircle, FileText } from 'lucide-react';

const MarkAsPaidConfirmationModal = ({ isOpen, onClose, onConfirm, invoice }) => {
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare payment data object with all fields
      const paymentData = {
        payment_date: paymentDate,
        payment_method: paymentMethod || undefined,
        payment_reference: paymentReference || undefined,
        notes: notes || undefined
      };
      
      await onConfirm(invoice._id, paymentData);
      onClose();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-grayteck-900 bg-opacity-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white dark:bg-darkbg-800 rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal header */}
          <div className="flex items-center mb-4">
            <div className="mr-3 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              Mark Invoice as Paid
            </h3>
          </div>

          {/* Modal content */}
          <div className="space-y-4">
            <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
              You are about to mark invoice #{invoice.invoice_number} for ${invoice.total.toFixed(2)} as paid.
              This action will update the invoice status and cannot be automatically reversed.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Payment Date - Required */}
              <div>
                <label 
                  htmlFor="payment-date" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Payment Date *
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <input
                    type="date"
                    id="payment-date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                    className="focus:ring-grayteck-500 focus:border-grayteck-500 block w-full pl-10 sm:text-sm 
                             border-grayteck-300 dark:border-grayteck-600 rounded-md bg-white dark:bg-darkbg-700 
                             text-grayteck-900 dark:text-grayteck-100 py-2"
                  />
                </div>
              </div>
              
              {/* Payment Method - Optional */}
              <div>
                <label 
                  htmlFor="payment-method" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Payment Method <span className="text-xs text-grayteck-500 dark:text-grayteck-400">(optional)</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <select
                    id="payment-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="focus:ring-grayteck-500 focus:border-grayteck-500 block w-full pl-10 sm:text-sm 
                             border-grayteck-300 dark:border-grayteck-600 rounded-md bg-white dark:bg-darkbg-700 
                             text-grayteck-900 dark:text-grayteck-100 py-2"
                  >
                    <option value="">Select a payment method</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              {/* Payment Reference - Optional */}
              <div>
                <label 
                  htmlFor="payment-reference" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Payment Reference <span className="text-xs text-grayteck-500 dark:text-grayteck-400">(optional)</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <input
                    type="text"
                    id="payment-reference"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Check #, Transaction ID, etc."
                    className="focus:ring-grayteck-500 focus:border-grayteck-500 block w-full pl-10 sm:text-sm 
                             border-grayteck-300 dark:border-grayteck-600 rounded-md bg-white dark:bg-darkbg-700 
                             text-grayteck-900 dark:text-grayteck-100 py-2"
                  />
                </div>
              </div>
              
              {/* Notes - Optional */}
              <div>
                <label 
                  htmlFor="payment-notes" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Notes <span className="text-xs text-grayteck-500 dark:text-grayteck-400">(optional)</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <FileText className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <textarea
                    id="payment-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    placeholder="Additional payment details or notes"
                    className="focus:ring-grayteck-500 focus:border-grayteck-500 block w-full pl-10 sm:text-sm 
                             border-grayteck-300 dark:border-grayteck-600 rounded-md bg-white dark:bg-darkbg-700 
                             text-grayteck-900 dark:text-grayteck-100 py-2"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 
                           rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 focus:outline-none"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none
                            flex items-center justify-center min-w-[100px] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    'Mark as Paid'
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MarkAsPaidConfirmationModal;